import type { ReactNode } from 'react';

import { useTelegram } from '@/shared/hooks/useTelegram';
import { cn } from '@/shared/lib/cn';

import { ChevronRightIcon } from '../lib/icons';

import styles from './ListRow.module.css';

export type ListRowTone = 'gold' | 'teal' | 'neutral' | 'danger';

export interface ListRowProps {
  title: string;
  subtitle?: string;
  value?: ReactNode;
  valueTone?: ListRowTone;
  icon?: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  className?: string;
}

export function ListRow({
  title,
  subtitle,
  value,
  valueTone = 'neutral',
  icon,
  onClick,
  chevron = false,
  className,
}: ListRowProps): React.ReactElement {
  const { haptic } = useTelegram();
  const interactive = typeof onClick === 'function';

  const content = (
    <>
      {icon !== undefined ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}

      <span className={styles.text}>
        <span className={styles.title}>{title}</span>
        {subtitle !== undefined ? <span className={styles.subtitle}>{subtitle}</span> : null}
      </span>

      {value !== undefined ? (
        <span className={cn(styles.value, styles[valueTone])}>{value}</span>
      ) : null}

      {chevron && interactive ? (
        <ChevronRightIcon className={styles.chevron} size={18} />
      ) : null}
    </>
  );

  if (!interactive) {
    return <div className={cn(styles.row, className)}>{content}</div>;
  }

  return (
    <button
      type="button"
      className={cn(styles.row, styles.interactive, className)}
      onClick={() => {
        haptic('light');
        onClick?.();
      }}
    >
      {content}
    </button>
  );
}
